import { ref, readonly } from 'vue'

const CONSENT_KEY = 'mvp_studio_cookie_consent'

/**
 * Cookie Consent Composable
 * 
 * Manages user consent for analytics and tracking cookies.
 * Persists consent state in localStorage.
 * 
 * Consent categories:
 * - necessary: Always enabled (auth, security, error monitoring)
 * - analytics: GA4, Mixpanel, Clarity (requires consent)
 * - marketing: Future use (requires consent)
 */

// Shared state across all instances
const consentState = ref(null)
const hasDecided = ref(false)

/**
 * Load consent from localStorage
 */
function loadConsent() {
  try {
    const stored = localStorage.getItem(CONSENT_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      consentState.value = parsed
      hasDecided.value = true
      return parsed
    }
  } catch (error) {
    console.error('[CookieConsent] Failed to load consent:', error)
  }
  return null
}

/**
 * Initialize consent state from storage
 */
function initConsent() {
  if (consentState.value === null) {
    loadConsent()
  }
}

export function useCookieConsent() {
  // Initialize on first use
  initConsent()

  /**
   * Check if user has made a consent decision
   */
  const hasUserDecided = () => {
    return hasDecided.value
  }

  /**
   * Get current consent state
   * @returns {Object|null} Consent object or null if not decided
   */
  const getConsent = () => {
    return consentState.value
  }

  /**
   * Check if a specific category is consented
   * @param {string} category - 'necessary', 'analytics', or 'marketing'
   * @returns {boolean}
   */
  const hasConsent = (category) => {
    if (category === 'necessary') return true // Always allowed
    if (!consentState.value) return false
    return consentState.value[category] === true
  }

  /**
   * Save consent preferences
   * @param {Object} preferences - { analytics: boolean, marketing: boolean }
   */
  const setConsent = (preferences) => {
    const consent = {
      necessary: true, // Always true
      analytics: preferences.analytics ?? false,
      marketing: preferences.marketing ?? false,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }

    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consent))
      consentState.value = consent
      hasDecided.value = true

      // Dispatch event for other parts of the app to react
      window.dispatchEvent(new CustomEvent('cookie-consent-updated', { 
        detail: consent 
      }))

      return consent
    } catch (error) {
      console.error('[CookieConsent] Failed to save consent:', error)
      return null
    }
  }

  /**
   * Accept all cookies
   */
  const acceptAll = () => {
    return setConsent({ analytics: true, marketing: true })
  }

  /**
   * Reject all optional cookies (only necessary)
   */
  const rejectAll = () => {
    return setConsent({ analytics: false, marketing: false })
  }

  /**
   * Reset consent (for testing or user request)
   */
  const resetConsent = () => {
    try {
      localStorage.removeItem(CONSENT_KEY)
      consentState.value = null
      hasDecided.value = false
    } catch (error) {
      console.error('[CookieConsent] Failed to reset consent:', error)
    }
  }

  return {
    consent: readonly(consentState),
    hasDecided: readonly(hasDecided),
    hasUserDecided,
    getConsent,
    hasConsent,
    setConsent,
    acceptAll,
    rejectAll,
    resetConsent
  }
}
